import React from "react";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Switch from "@mui/material/Switch";
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  IconButton,
  Input,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { createDots } from "../../common/createDots";
import {
  LIVESTREAM_URL_ERROR_MESSAGE,
  STREAM_SOURCE_OPTIONS,
  getStreamSourceName,
  isValidLivestreamUrl,
  maskLivestreamUrlPassword,
} from "./EditCameraUtils/livestreamUrlUtils";
import {
  SITE_CREDENTIALS_HELPER_TEXT,
  showCameraCredentialColumn,
  showStreamParametersColumn,
} from "./EditCameraUtils/streamPathIntegrations";
import {
  connectionFieldTextFieldSx,
  generalInfoTableCellSx,
  packIntoRows,
} from "./EditCameraUtils/editCameraGeneralInfoLayout";
import {
  showHostColumn,
  showRtspPortColumn,
  showVideoSourceColumn,
} from "./EditCameraUtils/connectionColumns";
import {
  GENETEC_DIRECT_PLACEHOLDERS,
  getCameraVideoSourceLabel,
  isGenetecDirectSite,
  isGenetecSite,
} from "./EditCameraUtils/genetecVideoSource";

/**
 * Read-only effective Video source for a camera on a Genetec site, with the
 * API's fallback reason (e.g. "No host set, streaming through the Media
 * Gateway") underneath. Same cell in view and edit mode.
 */
const VideoSourceCell = ({
  cameraGeneralInfo,
  siteInfo,
}: {
  cameraGeneralInfo: any;
  siteInfo: any;
}) => (
  <TableCell
    key="video_source"
    align="left"
    sx={generalInfoTableCellSx}
    data-testid="video-source-cell"
  >
    <div>{getCameraVideoSourceLabel(cameraGeneralInfo, siteInfo)}</div>
    {cameraGeneralInfo.video_source_reason ? (
      <div
        className="text-xs text-gray-500 mt-1"
        data-testid="video-source-reason"
      >
        {cameraGeneralInfo.video_source_reason}
      </div>
    ) : null}
  </TableCell>
);

/**
 * `rtsp` is an integer on the API. Blank drops the key from the PATCH (never
 * an empty string); anything else goes as a number when it parses as one, so
 * the server can reject a stray non-numeric value with its own message.
 */
export const applyRtspPortToPatch = (
  previousState: Record<string, any>,
  nextValue: string
): Record<string, any> => {
  const trimmed = String(nextValue ?? "").trim();
  if (trimmed === "") {
    const { rtsp: _omitted, ...rest } = previousState;
    return rest;
  }
  const port = Number(trimmed);
  return {
    ...previousState,
    rtsp: Number.isInteger(port) && /^\d+$/.test(trimmed) ? port : trimmed,
  };
};

type ConnectionHeaderRowsProps = {
  connectionTitles: string[];
};

export const GeneralInfoConnectionHeaderRows = ({
  connectionTitles,
}: ConnectionHeaderRowsProps) => (
  <>
    {packIntoRows(connectionTitles).map((titleRow, rowIndex) => (
      <TableRow key={`connection-header-${rowIndex}`}>
        {titleRow.map((title, columnIndex) => (
          <TableCell
            key={`connection-header-${rowIndex}-${columnIndex}`}
            align="left"
            sx={{ ...generalInfoTableCellSx, fontWeight: 600 }}
          >
            {title ?? ""}
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

type ConnectionViewRowsProps = {
  siteInfo: any;
  cameraGeneralInfo: any;
  userPermissions: { view_passwords?: boolean };
  showPassword: boolean;
  onToggleShowPassword: () => void;
  onMouseDownPassword: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseUpPassword: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

export const GeneralInfoConnectionViewRows = ({
  siteInfo,
  cameraGeneralInfo,
  userPermissions,
  showPassword,
  onToggleShowPassword,
  onMouseDownPassword,
  onMouseUpPassword,
}: ConnectionViewRowsProps) => {
  const cells: React.ReactNode[] = [];

  if (showVideoSourceColumn(siteInfo)) {
    cells.push(
      <VideoSourceCell
        key="video_source"
        cameraGeneralInfo={cameraGeneralInfo}
        siteInfo={siteInfo}
      />
    );
  }
  if (showHostColumn(cameraGeneralInfo, siteInfo)) {
    cells.push(
      <TableCell
        key="host"
        align="left"
        sx={generalInfoTableCellSx}
        data-testid="camera-host-cell"
      >
        {cameraGeneralInfo.ip ? cameraGeneralInfo.ip : "--"}
      </TableCell>
    );
  }
  if (showRtspPortColumn(siteInfo)) {
    cells.push(
      <TableCell
        key="rtsp"
        align="left"
        sx={generalInfoTableCellSx}
        data-testid="camera-rtsp-cell"
      >
        {cameraGeneralInfo.rtsp ? cameraGeneralInfo.rtsp : "--"}
      </TableCell>
    );
  }
  if (cameraGeneralInfo.http) {
    cells.push(
      <TableCell key="http" align="left" sx={generalInfoTableCellSx}>
        {cameraGeneralInfo.http}
      </TableCell>
    );
  }
  if (cameraGeneralInfo.server_port) {
    cells.push(
      <TableCell key="server_port" align="left" sx={generalInfoTableCellSx}>
        {cameraGeneralInfo.server_port}
      </TableCell>
    );
  }
  if (showCameraCredentialColumn("username", cameraGeneralInfo, siteInfo)) {
    cells.push(
      <TableCell
        key="username"
        align="left"
        sx={generalInfoTableCellSx}
        data-testid="camera-username-cell"
      >
        {cameraGeneralInfo.username ? cameraGeneralInfo.username : "--"}
      </TableCell>
    );
  }
  if (showCameraCredentialColumn("password", cameraGeneralInfo, siteInfo)) {
    cells.push(
      <TableCell
        key="password"
        align="left"
        sx={generalInfoTableCellSx}
        data-testid="camera-password-cell"
      >
        {!cameraGeneralInfo.password && "--"}
        {cameraGeneralInfo.password && userPermissions.view_passwords && (
          <div className="flex">
            {showPassword
              ? cameraGeneralInfo.password
              : createDots(cameraGeneralInfo.password)}
            <div>
              <InputAdornment position="end">
                <IconButton
                  aria-label={
                    showPassword ? "hide the password" : "display the password"
                  }
                  onClick={onToggleShowPassword}
                  onMouseDown={onMouseDownPassword}
                  onMouseUp={onMouseUpPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            </div>
          </div>
        )}
        {cameraGeneralInfo.password && !userPermissions.view_passwords && (
          <div className="flex items-center">
            {createDots(cameraGeneralInfo.password)}
          </div>
        )}
      </TableCell>
    );
  }
  if (cameraGeneralInfo.channel) {
    cells.push(
      <TableCell key="channel" align="left" sx={generalInfoTableCellSx}>
        {cameraGeneralInfo.channel}
      </TableCell>
    );
  }
  if (showStreamParametersColumn(cameraGeneralInfo, siteInfo)) {
    cells.push(
      <TableCell
        key="stream_parameters"
        align="left"
        sx={generalInfoTableCellSx}
        data-testid="stream-parameters-cell"
      >
        {cameraGeneralInfo.stream_parameters
          ? cameraGeneralInfo.stream_parameters
          : "--"}
      </TableCell>
    );
  }
  if (cameraGeneralInfo.slice) {
    cells.push(
      <TableCell key="slice" align="left" sx={generalInfoTableCellSx}>
        <Switch
          checked={cameraGeneralInfo.slice}
          color="warning"
          disabled={true}
        />
      </TableCell>
    );
  }
  if (cameraGeneralInfo.split) {
    cells.push(
      <TableCell key="split" align="left" sx={generalInfoTableCellSx}>
        <Switch
          checked={cameraGeneralInfo.split}
          color="warning"
          disabled={true}
        />
      </TableCell>
    );
  }
  if (cameraGeneralInfo.use_stream_quality) {
    cells.push(
      <TableCell key="stream_quality" align="left" sx={generalInfoTableCellSx}>
        {cameraGeneralInfo.stream_quality
          ? cameraGeneralInfo.stream_quality
          : 1}
      </TableCell>
    );
  }
  if (cameraGeneralInfo.stream_source) {
    cells.push(
      <TableCell key="stream_source" align="left" sx={generalInfoTableCellSx}>
        {getStreamSourceName(cameraGeneralInfo.stream_source)}
      </TableCell>
    );
    cells.push(
      <TableCell key="livestream_url" align="left" sx={generalInfoTableCellSx}>
        {cameraGeneralInfo.livestream_url
          ? maskLivestreamUrlPassword(cameraGeneralInfo.livestream_url)
          : "--"}
      </TableCell>
    );
  }

  return (
    <>
      {packIntoRows(cells).map((cellRow, rowIndex) => (
        <TableRow key={`connection-data-${rowIndex}`}>
          {cellRow.map(
            (cell, columnIndex) =>
              cell ?? (
                <TableCell
                  key={`connection-pad-${rowIndex}-${columnIndex}`}
                  align="left"
                  sx={generalInfoTableCellSx}
                />
              )
          )}
        </TableRow>
      ))}
    </>
  );
};

type ConnectionEditRowsProps = {
  siteInfo: any;
  cameraGeneralInfo: any;
  setCameraGeneralInfo: any;
  setPatchObject: any;
  setGeneralInfoChangesMade: any;
  userPermissions: { view_passwords?: boolean };
  showPassword: boolean;
  onToggleShowPassword: () => void;
  onMouseDownPassword: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseUpPassword: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleCredentialChange: (
    field: "username" | "password",
    nextValue: string
  ) => void;
  livestreamUrlError: boolean;
  setLivestreamUrlError: (value: boolean) => void;
};

export const GeneralInfoConnectionEditRows = ({
  siteInfo,
  cameraGeneralInfo,
  setCameraGeneralInfo,
  setPatchObject,
  setGeneralInfoChangesMade,
  userPermissions,
  showPassword,
  onToggleShowPassword,
  onMouseDownPassword,
  onMouseUpPassword,
  handleCredentialChange,
  livestreamUrlError,
  setLivestreamUrlError,
}: ConnectionEditRowsProps) => {
  const cells: React.ReactNode[] = [];
  const genetecDirect = isGenetecDirectSite(siteInfo);

  if (showVideoSourceColumn(siteInfo)) {
    cells.push(
      <VideoSourceCell
        key="video_source"
        cameraGeneralInfo={cameraGeneralInfo}
        siteInfo={siteInfo}
      />
    );
  }
  if (showHostColumn(cameraGeneralInfo, siteInfo)) {
    cells.push(
      <TableCell key="host" align="left" sx={generalInfoTableCellSx}>
        <TextField
          inputProps={{ "aria-label": "Host" }}
          defaultValue={cameraGeneralInfo.ip ?? ""}
          placeholder={
            genetecDirect ? GENETEC_DIRECT_PLACEHOLDERS.host : undefined
          }
          variant="standard"
          sx={connectionFieldTextFieldSx}
          type="string"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const nextValue = event.target.value;
            setCameraGeneralInfo((previousState: any) => ({
              ...previousState,
              ip: nextValue,
            }));
            setPatchObject((previousState: any) => ({
              ...previousState,
              ip: nextValue,
            }));
            setGeneralInfoChangesMade(true);
          }}
        />
      </TableCell>
    );
  }
  if (showRtspPortColumn(siteInfo)) {
    cells.push(
      <TableCell key="rtsp" align="left" sx={generalInfoTableCellSx}>
        <TextField
          inputProps={{ "aria-label": "RTSP", inputMode: "numeric" }}
          defaultValue={cameraGeneralInfo.rtsp ?? ""}
          placeholder={
            genetecDirect ? GENETEC_DIRECT_PLACEHOLDERS.rtsp : undefined
          }
          variant="standard"
          sx={connectionFieldTextFieldSx}
          type="string"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const nextValue = event.target.value;
            setCameraGeneralInfo((previousState: any) => ({
              ...previousState,
              rtsp: nextValue,
            }));
            setPatchObject((previousState: any) =>
              applyRtspPortToPatch(previousState, nextValue)
            );
            setGeneralInfoChangesMade(true);
          }}
        />
      </TableCell>
    );
  }
  if (cameraGeneralInfo.http) {
    cells.push(
      <TableCell key="http" align="left" sx={generalInfoTableCellSx}>
        <TextField
          defaultValue={cameraGeneralInfo.http}
          variant="standard"
          sx={connectionFieldTextFieldSx}
          type="string"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setCameraGeneralInfo((previousState: any) => ({
              ...previousState,
              http: event.target.value,
            }));
            setPatchObject((previousState: any) => ({
              ...previousState,
              http: event.target.value,
            }));
            setGeneralInfoChangesMade(true);
          }}
        />
      </TableCell>
    );
  }
  if (cameraGeneralInfo.server_port) {
    cells.push(
      <TableCell key="server_port" align="left" sx={generalInfoTableCellSx}>
        <TextField
          defaultValue={cameraGeneralInfo.server_port}
          variant="standard"
          sx={connectionFieldTextFieldSx}
          type="string"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setCameraGeneralInfo((previousState: any) => ({
              ...previousState,
              server_port: event.target.value,
            }));
            setPatchObject((previousState: any) => ({
              ...previousState,
              server_port: event.target.value,
            }));
            setGeneralInfoChangesMade(true);
          }}
        />
      </TableCell>
    );
  }
  if (showCameraCredentialColumn("username", cameraGeneralInfo, siteInfo)) {
    cells.push(
      <TableCell key="username" align="left" sx={generalInfoTableCellSx}>
        <TextField
          inputProps={{ "aria-label": "Username" }}
          defaultValue={cameraGeneralInfo.username ?? ""}
          variant="standard"
          sx={connectionFieldTextFieldSx}
          type="string"
          helperText={
            cameraGeneralInfo.username
              ? undefined
              : SITE_CREDENTIALS_HELPER_TEXT
          }
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            handleCredentialChange("username", event.target.value);
          }}
        />
      </TableCell>
    );
  }
  if (showCameraCredentialColumn("password", cameraGeneralInfo, siteInfo)) {
    cells.push(
      <TableCell key="password" align="left" sx={generalInfoTableCellSx}>
        <FormControl sx={connectionFieldTextFieldSx} variant="standard">
          <Input
            inputProps={{ "aria-label": "Password" }}
            type={showPassword ? "text" : "password"}
            defaultValue={cameraGeneralInfo.password ?? ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              handleCredentialChange("password", event.target.value);
            }}
            endAdornment={
              userPermissions.view_passwords ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showPassword
                        ? "hide the password"
                        : "display the password"
                    }
                    onClick={onToggleShowPassword}
                    onMouseDown={onMouseDownPassword}
                    onMouseUp={onMouseUpPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          />
          {!cameraGeneralInfo.password && (
            <FormHelperText>{SITE_CREDENTIALS_HELPER_TEXT}</FormHelperText>
          )}
        </FormControl>
      </TableCell>
    );
  }
  if (cameraGeneralInfo.channel) {
    cells.push(
      <TableCell key="channel" align="left" sx={generalInfoTableCellSx}>
        <TextField
          defaultValue={cameraGeneralInfo.channel}
          variant="standard"
          sx={connectionFieldTextFieldSx}
          type="string"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setCameraGeneralInfo((previousState: any) => ({
              ...previousState,
              channel: event.target.value,
            }));
            setPatchObject((previousState: any) => ({
              ...previousState,
              channel: event.target.value,
            }));
            setGeneralInfoChangesMade(true);
          }}
        />
      </TableCell>
    );
  }
  if (showStreamParametersColumn(cameraGeneralInfo, siteInfo)) {
    cells.push(
      <TableCell
        key="stream_parameters"
        align="left"
        sx={generalInfoTableCellSx}
      >
        <TextField
          inputProps={{ "aria-label": "Stream Parameters" }}
          defaultValue={cameraGeneralInfo.stream_parameters ?? ""}
          placeholder={
            isGenetecSite(siteInfo)
              ? GENETEC_DIRECT_PLACEHOLDERS.stream_parameters
              : "/Streaming/Channels/101"
          }
          variant="standard"
          sx={connectionFieldTextFieldSx}
          type="string"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const nextValue = event.target.value;
            setCameraGeneralInfo((previousState: any) => ({
              ...previousState,
              stream_parameters: nextValue,
            }));
            setPatchObject((previousState: any) => ({
              ...previousState,
              stream_parameters: nextValue,
            }));
            setGeneralInfoChangesMade(true);
          }}
        />
      </TableCell>
    );
  }
  if (cameraGeneralInfo.slice) {
    cells.push(
      <TableCell key="slice" align="left" sx={generalInfoTableCellSx}>
        <Switch
          defaultChecked={Boolean(cameraGeneralInfo.slice)}
          color="warning"
          disabled={false}
          onChange={(event: any) => {
            setCameraGeneralInfo((previousState: any) => ({
              ...previousState,
              slice: event.target.checked,
            }));
            setPatchObject((previousState: any) => ({
              ...previousState,
              slice: event.target.checked,
            }));
            setGeneralInfoChangesMade(true);
          }}
        />
      </TableCell>
    );
  }
  if (cameraGeneralInfo.split) {
    cells.push(
      <TableCell key="split" align="left" sx={generalInfoTableCellSx}>
        <Switch
          defaultChecked={Boolean(cameraGeneralInfo.split)}
          color="warning"
          disabled={false}
          onChange={(event: any) => {
            setCameraGeneralInfo((previousState: any) => ({
              ...previousState,
              split: event.target.checked,
            }));
            setPatchObject((previousState: any) => ({
              ...previousState,
              split: event.target.checked,
            }));
            setGeneralInfoChangesMade(true);
          }}
        />
      </TableCell>
    );
  }
  if (cameraGeneralInfo.use_stream_quality) {
    cells.push(
      <TableCell key="stream_quality" align="left" sx={generalInfoTableCellSx}>
        <TextField
          defaultValue={cameraGeneralInfo.stream_quality}
          variant="standard"
          sx={connectionFieldTextFieldSx}
          type="string"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setCameraGeneralInfo((previousState: any) => ({
              ...previousState,
              stream_quality: event.target.value,
            }));
            setPatchObject((previousState: any) => ({
              ...previousState,
              stream_quality: event.target.value,
            }));
            setGeneralInfoChangesMade(true);
          }}
        />
      </TableCell>
    );
  }
  if (cameraGeneralInfo.stream_source) {
    cells.push(
      <TableCell key="stream_source" align="left" sx={generalInfoTableCellSx}>
        <Autocomplete
          disablePortal
          disableClearable
          options={STREAM_SOURCE_OPTIONS.map((option: any) => option.name)}
          defaultValue={getStreamSourceName(cameraGeneralInfo.stream_source)}
          onChange={(event: any, value: any) => {
            const newStreamSource =
              STREAM_SOURCE_OPTIONS.find((option: any) => option.name === value)
                ?.value ?? "connector";
            setCameraGeneralInfo((previousState: any) => ({
              ...previousState,
              stream_source: newStreamSource,
            }));
            setPatchObject((previousState: any) => {
              if (newStreamSource !== "external_url") {
                const { livestream_url: _omitted, ...rest } = previousState;
                return { ...rest, stream_source: newStreamSource };
              }
              return { ...previousState, stream_source: newStreamSource };
            });
            if (newStreamSource !== "external_url") {
              setLivestreamUrlError(false);
            }
            setGeneralInfoChangesMade(true);
          }}
          sx={connectionFieldTextFieldSx}
          renderInput={(params) => (
            <TextField {...params} variant="standard" sx={{}} />
          )}
        />
      </TableCell>
    );
    cells.push(
      <TableCell key="livestream_url" align="left" sx={generalInfoTableCellSx}>
        {cameraGeneralInfo.stream_source === "external_url" ? (
          <TextField
            defaultValue=""
            placeholder={
              cameraGeneralInfo.livestream_url
                ? maskLivestreamUrlPassword(cameraGeneralInfo.livestream_url)
                : "rtsp://user:pass@host/stream"
            }
            error={livestreamUrlError}
            helperText={
              livestreamUrlError
                ? LIVESTREAM_URL_ERROR_MESSAGE
                : cameraGeneralInfo.livestream_url
                ? "Leave blank to keep the current URL."
                : ""
            }
            variant="standard"
            sx={connectionFieldTextFieldSx}
            type="string"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const newUrl = event.target.value;
              setLivestreamUrlError(
                newUrl !== "" && !isValidLivestreamUrl(newUrl)
              );
              setPatchObject((previousState: any) => {
                if (newUrl === "") {
                  const { livestream_url: _omitted, ...rest } = previousState;
                  return rest;
                }
                return { ...previousState, livestream_url: newUrl };
              });
              setGeneralInfoChangesMade(true);
            }}
          />
        ) : (
          "--"
        )}
      </TableCell>
    );
  }

  return (
    <>
      {packIntoRows(cells).map((cellRow, rowIndex) => (
        <TableRow key={`connection-edit-${rowIndex}`}>
          {cellRow.map(
            (cell, columnIndex) =>
              cell ?? (
                <TableCell
                  key={`connection-edit-pad-${rowIndex}-${columnIndex}`}
                  align="left"
                  sx={generalInfoTableCellSx}
                />
              )
          )}
        </TableRow>
      ))}
    </>
  );
};
